/**
 * Utility functions for handling keyboard interactions
 *
 * iOS 26 Liquid Glass: We use Capacitor keyboard resize mode 'none' to prevent
 * the black box issue behind the rounded glass keyboard. This means the keyboard
 * overlays content instead of resizing the webview.
 *
 * Keyboard show/hide handling is done globally in App.tsx via Capacitor events.
 * This file provides utilities for component-specific keyboard needs.
 */

/**
 * Scrolls the focused element into view with proper offset for keyboard.
 * Use this when an input gains focus and needs to be visible above the keyboard.
 */
export const scrollInputIntoView = (element: HTMLElement | null, keyboardHeight = 300) => {
  if (!element) return;

  // Add temporary padding to allow scrolling
  const originalPadding = document.body.style.paddingBottom;
  document.body.style.paddingBottom = `${keyboardHeight}px`;

  // Scroll with a slight delay for smooth animation
  setTimeout(() => {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 50);

  // Note: padding is removed by the global keyboardWillHide listener in App.tsx
  // If using outside of native context, restore padding manually:
  // setTimeout(() => { document.body.style.paddingBottom = originalPadding; }, 500);
};

/**
 * Handles focus event - scrolls input into view for keyboard overlay mode
 * @param e - Focus event
 */
export const handleInputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  // Only need special handling on native platforms with overlay keyboard
  // The global listener in App.tsx handles this automatically
  // This is kept for cases where components need explicit control
};
